/**
 * Cycles Page - Constants and Utilities
 */

import { Timestamp } from 'firebase/firestore';
import type { Cycle } from '../../models';
import { formatDateRangeIgnoringTimezone } from '../../utils/dateFormatting';

/**
 * Mock cycles data (3 cycles)
 */
export const MOCK_CYCLES: Cycle[] = [
  {
    id: 'cycle-1',
    name: 'Q1 2025',
    description: 'Primeiro trimestre de 2025 - Crescimento e Expansão',
    startDate: Timestamp.fromDate(new Date('2025-01-01')),
    endDate: Timestamp.fromDate(new Date('2025-03-31')),
    durationMonths: 3,
    status: 'active',
    maxObjectivesPerUser: 4,
    checkInFrequency: 'weekly',
    objectiveCount: 15,
    activeObjectiveCount: 12,
    completionRate: 45,
    createdBy: 'admin',
    createdAt: Timestamp.fromDate(new Date('2024-12-15')),
    updatedAt: Timestamp.fromDate(new Date('2025-01-01')),
  },
  {
    id: 'cycle-2',
    name: 'Q4 2024',
    description: 'Quarto trimestre de 2024 - Fechamento do Ano',
    startDate: Timestamp.fromDate(new Date('2024-10-01')),
    endDate: Timestamp.fromDate(new Date('2024-12-31')),
    durationMonths: 3,
    status: 'completed',
    maxObjectivesPerUser: 4,
    checkInFrequency: 'weekly',
    objectiveCount: 18,
    activeObjectiveCount: 0,
    completionRate: 100,
    createdBy: 'admin',
    createdAt: Timestamp.fromDate(new Date('2024-09-15')),
    updatedAt: Timestamp.fromDate(new Date('2025-01-05')),
  },
  {
    id: 'cycle-3',
    name: 'Q3 2024',
    description: 'Terceiro trimestre de 2024 - Consolidação',
    startDate: Timestamp.fromDate(new Date('2024-07-01')),
    endDate: Timestamp.fromDate(new Date('2024-09-30')),
    durationMonths: 3,
    status: 'completed',
    maxObjectivesPerUser: 4,
    checkInFrequency: 'weekly',
    objectiveCount: 16,
    activeObjectiveCount: 0,
    completionRate: 100,
    createdBy: 'admin',
    createdAt: Timestamp.fromDate(new Date('2024-06-15')),
    updatedAt: Timestamp.fromDate(new Date('2024-10-05')),
  },
];

/**
 * Get cycle status badge classes
 */
export function getCycleStatusClasses(status: Cycle['status']): string {
  switch (status) {
    case 'planning':
      return 'bg-gray-100 text-gray-700 border-gray-300';
    case 'active':
      return 'bg-green-100 text-green-700 border-green-300';
    case 'reviewing':
      return 'bg-purple-100 text-purple-700 border-purple-300';
    case 'completed':
      return 'bg-blue-100 text-blue-700 border-blue-300';
    default:
      return 'bg-gray-100 text-gray-700 border-gray-300';
  }
}

/**
 * Get cycle status label
 */
export function getCycleStatusLabel(status: Cycle['status']): string {
  switch (status) {
    case 'planning':
      return 'Planejamento';
    case 'active':
      return 'Ativo';
    case 'reviewing':
      return 'Em Revisão';
    case 'completed':
      return 'Encerrado';
    default:
      return 'Desconhecido';
  }
}

/**
 * Calculate cycle progress percentage
 */
export function calculateCycleProgress(startDate: Timestamp | Date, endDate: Timestamp | Date): number {
  const now = new Date();
  const startDateObj = startDate instanceof Timestamp ? startDate.toDate() : startDate;
  const endDateObj = endDate instanceof Timestamp ? endDate.toDate() : endDate;
  const total = endDateObj.getTime() - startDateObj.getTime();
  const elapsed = now.getTime() - startDateObj.getTime();

  if (elapsed <= 0) return 0;
  if (elapsed >= total) return 100;

  return Math.round((elapsed / total) * 100);
}

/**
 * Format date range
 *
 * Note: Uses formatDateRangeIgnoringTimezone from utils to avoid timezone issues.
 * Since cycle dates represent calendar dates only (no specific time), we ignore
 * timezone to ensure dates are displayed as entered by the user.
 */
export function formatDateRange(startDate: Timestamp | Date, endDate: Timestamp | Date): string {
  const options: Intl.DateTimeFormatOptions = { day: '2-digit', month: 'short', year: 'numeric' };
  return formatDateRangeIgnoringTimezone(startDate, endDate, options);
}
